import { Box, FormLabel, Grid, Skeleton, styled, Typography } from "@mui/material";
import { StaticDatePicker, PickerDay, PickerDayProps } from '@mui/x-date-pickers-pro';
import { DateTime } from "luxon";
import { ReactElement, useState, useEffect } from "react";
import colors from "../../../colors";
import TimeSlots from "./TimeSlots";
import { BookingSlot, BookingSlotsByDay } from "../../../store";
import { CabButton } from "@CabComponents/CabButton";
import { CabTimezoneInput } from "@CabComponents/CabTimezoneInput";
import CabSpinner from "@CabComponents/CabSpinner";
import { CabIcon } from "@CabComponents/CabIcon";
import { IoBan, IoCalendar } from "react-icons/io5";
// import MobileDateTimeSelection from "./MobileDateTimeSelection";


// const dayFormat = 'cccc MMMM d, yyyy';
export const DateTimeSelectionHeader = ({
  from, isRecurring
}: { from: string; isRecurring?: boolean }) => (
  <><b>{from}</b> has invited you to a {isRecurring ? "recurring " : ""}meeting</>
);

export const DateTimeSelectionSubHeader = ({ isRecurring }: { isRecurring?: boolean }) => (
  isRecurring ? <>Select date and time to preview the recurring schedule</> : null
);

export interface DateTimeSelectionProps {
  selectedDay: DateTime | null;
  orderedAvailableDates: DateTime[];
  recurringVisibleDates?: DateTime[];
  selectedRecurringSeriesDates?: DateTime[];
  slotsByDay: BookingSlotsByDay;
  currentTimezone: string;
  from: string;
  slotSelected: BookingSlot | null;
  isPreview?: boolean;
  hideTitle?: boolean;
  setCurrentTimeZone: (tz: string) => void;
  handleDaySelected: (daySelected: DateTime | null) => void;
  setSlotSelected: (slot: BookingSlot) => void;
  onConfirm: () => void;
  onNoTimesWork: () => void;
  isFramed?: boolean;
  onCalendarDateChange?: (date: DateTime) => void
  loading?: boolean;
  defaultYearMonth: [number, number] | null
  viewDate: DateTime;
  isEnterpriseBook?: boolean;
  showSkeletons?: boolean;
  isSmDown?: boolean;
  isSelectedDay?: boolean;
}

const DateTimeSelection = ({
  selectedDay, orderedAvailableDates, recurringVisibleDates = [], selectedRecurringSeriesDates = [],
  slotsByDay, currentTimezone, from, slotSelected, isPreview, hideTitle, setCurrentTimeZone,
  handleDaySelected, setSlotSelected, onConfirm, onNoTimesWork, isFramed, onCalendarDateChange, loading,
  defaultYearMonth, viewDate, isEnterpriseBook, showSkeletons, isSmDown, isSelectedDay
}: DateTimeSelectionProps) => {
  const [showTimeSlots, setShowTimeSlots] = useState(false);

  const selectedDayIsAvailable = selectedDay && orderedAvailableDates.some(d => d.hasSame(selectedDay, 'day'));

  // Update showTimeSlots whenever selectedDay changes on mobile
  useEffect(() => {
    if (isSmDown) {
      setShowTimeSlots(!!selectedDay);
    }
  }, [isSmDown, selectedDay]);

  const handleSetCurrentTimeZone = (value: string | null) => {
    if (value != null) {
      setCurrentTimeZone(value);
    }
  };

  const handleBackToDatePicker = () => {
    setShowTimeSlots(false);
  };
  
  /* if (isSmDown) {
    return (
      <MobileDateTimeSelection
        selectedDay={selectedDay}
        orderedAvailableDates={orderedAvailableDates}
        currentTimezone={currentTimezone}
        slotSelected={slotSelected}
        from={from}
        isPreview={isPreview}
        setCurrentTimeZone={setCurrentTimeZone}
        handleDaySelected={handleDaySelected}
        slotsByDay={slotsByDay}
        setSlotSelected={setSlotSelected}
        onConfirm={onConfirm}
        isSelectedDay={isSelectedDay}
        isFramed={isFramed}
        onCalendarDateChange={onCalendarDateChange}
        loading={!!loading}
        defaultYearMonth={defaultYearMonth}
        setNoTimesWork={setNoTimesWork}
        showSkeletons={showSkeletons}
      />
    );
  } */
 
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: { md: 640 }
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
        <Grid container spacing={4} rowSpacing={3}>
          <Grid size={{ xs: 12, sm: 6.2 }}>
            <Box
              sx={{
                width: '100%',
                maxWidth: isSmDown ? 324 : 'none'
              }}>
              <FormLabel htmlFor="timezone-select">Timezone</FormLabel>
              <CabTimezoneInput
                id="timezone-select"
                label='Timezone'
                value={currentTimezone ? currentTimezone : ''}
                required={true}
                defaultValue={currentTimezone ? currentTimezone : ''}
                onChange={handleSetCurrentTimeZone}
                sx={{ width: '100%' }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 5.8 }} />

          {isSmDown ? (
            <>
              {showTimeSlots ? (
                <Grid size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}>
                    <Box 
                      onClick={handleBackToDatePicker}
                      sx={{
                        width: '100%',
                        maxWidth: isSmDown ? 324 : 'none',
                        cursor: 'pointer',
                        border: `1px solid ${colors.black200}`,
                        borderRadius: 1,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          borderColor: colors.black400,
                        }
                      }}
                    >
                      <CabIcon Icon={IoCalendar} size={20} color={colors.black900} />
                      <Typography color={colors.black700}>
                        {selectedDay?.toLocaleString({
                          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 324,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        maxHeight: 338
                      }}>
                      {showSkeletons ? (
                        <Skeleton height='100%' width='100%' variant='rounded' sx={{minHeight: '300px'}}/>
                      ) : (
                        <>
                          {!loading ? (
                            <ScrollBox>
                              <TimeSlots
                                slots={selectedDay
                                  ? slotsByDay[selectedDay.startOf('day').toMillis()] || []
                                  : []}
                                currentTimeZone={currentTimezone}
                                slotSelected={slotSelected}
                                setSlotSelected={setSlotSelected}
                                onConfirm={onConfirm}
                              />
                            </ScrollBox>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center"
                              }}>
                              <CabSpinner scale={4} color='inherit' data-testid="calendar-spinner"/>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ) : (
                <Grid size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      height: '100%'
                    }}>
                    {showSkeletons ? (
                      <Skeleton height='100%' width='100%' variant='rounded' />
                    ) : (
                      <StaticDayPicker
                        selectedDay={selectedDay}
                        orderedAvailableDates={orderedAvailableDates}
                        recurringVisibleDates={recurringVisibleDates}
                        selectedRecurringSeriesDates={selectedRecurringSeriesDates}
                        onDaySelected={handleDaySelected}
                        onCalendarDateChange={onCalendarDateChange}
                        defaultYearMonth={defaultYearMonth}
                        timezone={currentTimezone}
                        slotsLoading={!!loading}
                      />
                    )}
                  </Box>
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid size={6.2}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    height: '100%'
                  }}>
                  {showSkeletons ? (
                    <Skeleton height='100%' width='100%' variant='rounded' />
                  ) : (
                    <StaticDayPicker
                      selectedDay={selectedDay}
                      orderedAvailableDates={orderedAvailableDates}
                      recurringVisibleDates={recurringVisibleDates}
                      selectedRecurringSeriesDates={selectedRecurringSeriesDates}
                      onDaySelected={handleDaySelected}
                      onCalendarDateChange={onCalendarDateChange}
                      defaultYearMonth={defaultYearMonth}
                      timezone={currentTimezone}
                      slotsLoading={!!loading}
                    />
                  )}
                </Box>
              </Grid>

              <Grid size={5.8}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxHeight: 338
                  }}>
                  {showSkeletons ? (
                    <Skeleton height='100%' width='100%' variant='rounded' sx={{minHeight: '300px'}}/>
                  ) : (
                    <>
                      {!loading ? (
                        <ScrollBox>
                          {selectedDayIsAvailable ? (
                            <TimeSlots
                              slots={selectedDay
                                ? slotsByDay[selectedDay.startOf('day').toMillis()] || []
                                : []}
                              currentTimeZone={currentTimezone}
                              slotSelected={slotSelected}
                              setSlotSelected={setSlotSelected}
                              onConfirm={onConfirm}
                            />
                          ) : (
                            orderedAvailableDates.length ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  height: '100%',
                                  width: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                No Day Selected
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  height: '100%',
                                  width: '100%',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  gap: 1
                                }}>
                                <Typography sx={{
                                  fontSize: 15
                                }}>
                                  There are no available times in {viewDate.toLocaleString({ month: 'long' })}
                                </Typography>
                                {onCalendarDateChange && (
                                  <CabButton
                                    onClick={() => onCalendarDateChange(viewDate.plus({ month: 1 }).set({ day: 1 }))}
                                    buttonType='tertiary'
                                  >
                                    Check Next Month
                                  </CabButton>
                                )}
                              </Box>
                            )
                          )}
                        </ScrollBox>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                          <CabSpinner scale={4} color='inherit' data-testid="calendar-spinner"/>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            </>
          )}
          
          <Grid size={{ xs: 0, sm: 5.8 }} sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Grid size={{ xs: 12, sm: 6.2 }}>
            <Box
              sx={{
                bgcolor: "white",
                width: '100%',
                maxWidth: { xs: 324, sm: 'none' },
                paddingLeft: { xs: 0, sm: 3 },
                paddingRight: { xs: 0, sm: 1.5 }
              }}>
              <CabButton
                onClick={onNoTimesWork}
                buttonType="secondary"
                sx={{ width: '100%', borderStyle: 'dashed', height: 44, color: colors.black700 }}
                icon={<CabIcon Icon={IoBan} size={16} />}
              >
                None of these times work
              </CabButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DateTimeSelection;

interface StaticDayPickerProps {
  selectedDay: DateTime | null;
  noDefault?: boolean;
  orderedAvailableDates: DateTime[];
  recurringVisibleDates?: DateTime[];
  selectedRecurringSeriesDates?: DateTime[];
  onDaySelected: (daySelected: DateTime | null) => void;
  onCalendarDateChange?: (date: DateTime) => void;
  defaultYearMonth: [number, number]|null;
  timezone?: string;
  slotsLoading: boolean;
}

const StaticDayPicker = ({
  selectedDay, onDaySelected, orderedAvailableDates, recurringVisibleDates = [], selectedRecurringSeriesDates = [],
  noDefault, defaultYearMonth, timezone, onCalendarDateChange = () => undefined, slotsLoading
}: StaticDayPickerProps): ReactElement => {
  const disableDate = (date: DateTime) => {
    return !orderedAvailableDates.find(d => d.hasSame(date, 'day'));
  };
  const isRecurringVisibleDate = (date: DateTime) => (
    recurringVisibleDates.some(d => d.hasSame(date, 'day'))
  );
  const isSelectedRecurringSeriesDate = (date: DateTime) => (
    selectedRecurringSeriesDates.some(d => d.hasSame(date, 'day'))
  );

  const value = noDefault || !selectedDay ? null : selectedDay.setZone(timezone);
  const selectedDateIsDisabled = value ? disableDate(value) : false;

  const CustomDay = (props: PickerDayProps) => {
    const { day, ...other } = props;
    const isSelected = selectedDay && day.hasSame(selectedDay, 'day');
    const isDisabled = disableDate(day);
    const isVisibleRecurringDate = isRecurringVisibleDate(day);
    const isSelectedSeriesDate = isSelectedRecurringSeriesDate(day);
    const disabledRecurringBackground = isSelectedSeriesDate
      ? colors.lavendar200
      : isVisibleRecurringDate ? colors.lavendar50 : 'inherit';

    return (
      <PickerDay
        {...other}
        day={day}
        selected={!!isSelected}
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) {
            onDaySelected(day);
          }
        }}
        sx={{
          backgroundColor: isDisabled ? disabledRecurringBackground : colors.lavendar200,
          '&:hover, &:focus': {
            backgroundColor: isDisabled ? disabledRecurringBackground : colors.lavendar500,
          },
          '&.MuiPickerDay-root.Mui-selected': {
            backgroundColor: colors.lavendar950,
            color: selectedDateIsDisabled ? colors.black700 : undefined,
            '&:hover': {
              backgroundColor: colors.lavendar800,
            }
          },
          '&.MuiPickerDay-root.Mui-disabled': {
            backgroundColor: disabledRecurringBackground,
            color: isVisibleRecurringDate || isSelectedSeriesDate ? colors.black700 : 'inherit',
            opacity: 1,
          },
        }}
      />
    );
  };

  return <Box data-testid="static-date-picker" sx={{
    '& .MuiPickersArrowSwitcher-root .MuiButtonBase-root': {
      backgroundColor: colors.lavendar200,
      '&:hover': {
        backgroundColor: colors.lavendar500
      },
      '&.Mui-disabled': {
        backgroundColor: 'inherit'
      }
    }
  }}>
    {defaultYearMonth && (
      <StaticDatePicker
        sx={{ border: `1px solid ${colors.black200}`, borderRadius: 1 }}
        key={timezone}
        timezone={timezone}
        displayStaticWrapperAs='desktop'
        disablePast
        disableHighlightToday
        disabled={slotsLoading}
        openTo="day"
        onYearChange={(date: DateTime) => {
          const nextDate = DateTime.fromISO(date.toISODate() || "", { zone: timezone });
          onCalendarDateChange(nextDate);
          onDaySelected(null);
        }}
        onMonthChange={(date: DateTime) => {
          const nextDate = DateTime.fromISO(date.toISODate() || "", { zone: timezone });
          onCalendarDateChange(nextDate);
          onDaySelected(null);
        }}
        value={value}
        shouldDisableDate={disableDate}
        onChange={onDaySelected}
        referenceDate={DateTime.fromObject({ year: defaultYearMonth[0], month: defaultYearMonth[1] })}
        slots={{
          day: CustomDay
        }}
        slotProps={{
          actionBar: {
            actions: [],
          },
          previousIconButton: slotsLoading === true ? { disabled: true } : undefined,
          nextIconButton: { disabled: slotsLoading === true ? true : undefined }
        }}
      />
    )}
  </Box>;
};

const ScrollBox = styled(Box, { label: "ScrollBox" })({
  width: '100%',
  overflow: 'auto',
  // height: "fit-content",
  // marginBottom: 12,
  // maxHeight: 280,
  overflowX: 'hidden',
  overflowY: 'auto',
  '::-webkit-scrollbar': {
    width: 8,
    height: 8,
    backgroundColor: colors.white900,
  },
  '::-webkit-scrollbar-thumb': {
    background: colors.black300,
    borderRadius: 4,
  }
});
